import { useState } from "react";
import axios from "axios";
import {
  sendOTP as sendOTPApi,
  verifyOTP as verifyOTPApi,
} from "../services/AuthService";
import { useAuth } from "../context/AuthContext";

export interface IPhoneVerification {
  setPhoneNumber: (number: string) => void;
  verificationStatus: VerificationStatus;
  verificationError: string;
  getOTP: () => void;
  verifyOTP: (otp: string) => Promise<void>;
  setVerificationStatus: (status: VerificationStatus) => void;
  clearVerificationError: () => void;
}

export enum VerificationStatus {
  UNVERIFIED = "UNVERIFIED",
  VERIFIED = "VERIFIED",
  SMS_SENT = "SMS_SENT",
  VERIFICATION_FAILED = "VERIFICATION_FAILED",
  RESEND_OTP_REQUIRED = "RESEND_OTP_REQUIRED",
  VERIFYING = "VERIFYING",
}

const usePhoneVerification = (): IPhoneVerification => {
  const { loginUser } = useAuth();

  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [verificationError, setVerificationError] = useState<string>("");
  const [verificationStatus, setVerificationStatus] =
    useState<VerificationStatus>(VerificationStatus.UNVERIFIED);

  const getErrorMessage = (error: unknown, fallback: string): string => {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as
        | { message?: string; debugMessage?: string }
        | undefined;
      if (data?.debugMessage) {
        return data.debugMessage;
      }
      if (data?.message) {
        return data.message;
      }
    }
    return fallback;
  };

  const getOTP = () => {
    setVerificationError("");
    sendOTPApi(phoneNumber)
      .then(() => {
        setVerificationStatus(VerificationStatus.SMS_SENT);
      })
      .catch((error) => {
        setVerificationError(
          getErrorMessage(
            error,
            "Could not send OTP for this number. Please check admin access."
          )
        );
        setVerificationStatus(VerificationStatus.UNVERIFIED);
      });
  };

  const verifyOTP = async (code: string): Promise<void> => {
    setVerificationError("");
    setVerificationStatus(VerificationStatus.VERIFYING);
    try {
      const response = await verifyOTPApi(phoneNumber, code);
      const { accessToken, refreshToken } = response.data.data;
      loginUser(accessToken, refreshToken);
      setVerificationStatus(VerificationStatus.VERIFIED);
    } catch (error) {
      console.log("### Error in verifying OTP: ", error);
      setVerificationError(
        getErrorMessage(error, "Incorrect OTP or admin access is not allowed.")
      );
      setVerificationStatus(VerificationStatus.VERIFICATION_FAILED);
    }
  };

  return {
    setPhoneNumber,
    verifyOTP,
    verificationStatus,
    verificationError,
    getOTP,
    setVerificationStatus,
    clearVerificationError: () => setVerificationError(""),
  };
};

export default usePhoneVerification;
